import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { CopyToClipboard } from './CopyToClipboard';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { GridContainer } from './GridContainer';
import { ScreenSize } from '../types/screenSize';
import { useScreenSize } from '../hooks/useScreenSize';
import { LanguageSwitcherComponent } from '@pec/aion-ui-i18next/components/LanguageSwitcher';
import { Feature } from 'flagged';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';
import { languageSwitcherParentTypes, localizationFeatureFlag } from '@pec/aion-ui-i18next/constants';
import { LanguageSwitcherParent } from '@pec/aion-ui-i18next/types';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridItem: ({ smallDown }: ScreenSize) => ({
      padding: theme.spacing(1),
      textAlign: 'center',
      [smallDown]: {
        padding: theme.spacing(2)
      }
    })
  })
);

type Props = {
  typography?: string;
  showLanguageSwitcher?: boolean;
  traceHeaderValue: string | null;
  userId?: string;
};

export const FooterComponent: React.FC<Props> = ({
  typography = '',
  traceHeaderValue,
  showLanguageSwitcher,
  userId
}) => {
  const { isSmallDown, smallDown } = useScreenSize();
  const classes = useStyles({ smallDown });
  const { t } = useTranslation();
  const languageSwitcherParent = showLanguageSwitcher
    ? languageSwitcherParentTypes.registration
    : languageSwitcherParentTypes.footer;
  const renderLanguageSwitcherComponent = (parent: LanguageSwitcherParent) => (
    <FeatureFlagWrapper>
      <Feature
        name={localizationFeatureFlag}
        render={(isEnabled: boolean) => isEnabled && <LanguageSwitcherComponent parent={parent} userId={userId} />}
      />
    </FeatureFlagWrapper>
  );

  return isSmallDown ? (
    <GridContainer
      spacing={0}
      style={{ flexGrow: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <Grid item xs={12} className={classes.gridItem}>
        {renderLanguageSwitcherComponent(languageSwitcherParent)}
        <Typography variant="body2" className={typography}>
          {t('components.footer.veriforceFooterSimple', {
            defaultValue: '©{{year}} Veriforce.',
            year: new Date().getFullYear()
          })}{' '}
          {traceHeaderValue && <CopyToClipboard valueToCopy={traceHeaderValue} />}
        </Typography>
      </Grid>
    </GridContainer>
  ) : (
    <GridContainer spacing={0} style={{ flexGrow: 1, display: 'flex', alignItems: 'flex-end' }}>
      <Grid item xs={12} className={classes.gridItem}>
        {showLanguageSwitcher && renderLanguageSwitcherComponent(languageSwitcherParent)}
        <Typography variant="body2" className={typography}>
          {t('components.footer.veriforceFooter', {
            defaultValue: '©{{year}} Veriforce. All Rights Reserved.',
            year: new Date().getFullYear()
          })}{' '}
          | <Link href="/SSQV4/HelpListing.aspx">{t('components.footer.help', 'Help')}</Link>
        </Typography>
        {traceHeaderValue && (
          <Typography variant="body2" className={typography} noWrap>
            {t('components.footer.correlationID', {
              defaultValue: 'Correlation ID: {{traceHeaderValue}}',
              traceHeaderValue
            })}
            <CopyToClipboard valueToCopy={traceHeaderValue} />
          </Typography>
        )}
      </Grid>
    </GridContainer>
  );
};
